import {Component, inject, Input} from '@angular/core';
import {JobStatusItem} from "../../../core/definitions/job-status-item";
import {MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle} from "@angular/material/expansion";
import {MatAnchor, MatButton} from "@angular/material/button";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {RouterLink} from "@angular/router";
import {PrimusStateMapperService} from "../../../core/primus-state-mapper.service";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {CmsApiService} from "../../../core/cms-api.service";
import {FileSaverService} from "ngx-filesaver";
import {LoggerService} from "../../../core/logger.service";
import {saveAs} from "file-saver";
import {RefService} from "../../../core/ref.service";
import {SuperObjectModel} from "../../../core/definitions/super-object-model";
import {MetaOperationObjectStatus} from "../../../core/definitions/meta-operation-object-statuses";
import {FeatureFlagsService} from "../../../core/feature-flags.service";
import {OperationExecutionStatus} from "../../../core/definitions/operation-execution-status.enum";
import {RefParams} from "../../../core/definitions/ref-params";

@Component({
  selector: 'app-job-status-item',
  imports: [
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatButton,
    TranslateModule,
    MatAnchor,
    RouterLink,
    MatExpansionPanelHeader,
    MatIcon,
    MatProgressSpinner
  ],
  templateUrl: './job-status-item.component.html',
  styleUrl: './job-status-item.component.scss'
})
export class JobStatusItemComponent {
  // Injections
  public primusStateMapperService: PrimusStateMapperService = inject(PrimusStateMapperService);
  public readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly fileSaverService: FileSaverService = inject(FileSaverService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly refService: RefService = inject(RefService);

  @Input() message: JobStatusItem;

  detailsStartIndex = 0;
  detailsRows = 10;
  detailsStatusType = null;
  retryingAll = false;

  /**
   * Downloads the requested report to the client.
   * @param reportId
   * @param fileType
   */
  downloadReport = (reportId: string, fileType: string) => {
    this.cms.downloadReport(reportId).subscribe({
      next: (blob) => {
        this.fileSaverService.save(blob, reportId + '.' + fileType);
      },
      error: (error) => {
        this.logger.error('Error downloading report', error);
      }
    });
  }

  async downloadReportV2(reportId: string) {
    let downloadUrl = await this.cms.requestReportPdfFromDms(reportId);
    if (downloadUrl.url) {
      let pdfFile = await fetch(downloadUrl.url);
      let pdfFileBlob = await pdfFile.blob();
      saveAs(pdfFileBlob, reportId + '.pdf');
    }
  }

  stopJob() {
    this.cms.stopOperation(this.message.queue_id).then((res) => {
      this.logger.info('Job stopped successfully', res);
      this.message.status = 'stopped';
    }).catch(error => {
      this.logger.error('Error stopping job', error);
    });
  }

  continueJob() {
    this.cms.continueOperation(this.message.queue_id).then((res) => {
      this.logger.info('Job continued successfully', res);
      this.message.status = OperationExecutionStatus.IN_PROGRESS;
    }).catch(error => {
      this.logger.error('Error continuing job', error);
    });
  }

  cancelJob() {
    this.cms.cancelOperation(this.message.queue_id).then((res) => {
      this.logger.info('Job cancelled successfully', res);
      this.message.status = 'cancelled';
      this.message.statusType = 'cancelled';
      this.message.statusMessage = this.translate.instant('TRANS__JOB_STATUS__CANCELLED');
    }).catch(error => {
      this.logger.error('Error cancelling job', error);
    });
  }

  deleteJob() {
    this.cms.deleteOperation(this.message.queue_id).then((res) => {
      this.logger.info('Job deleted successfully', res);
      this.message.status = 'deleted';
      this.message.statusType = 'deleted';
      this.message.statusMessage = this.translate.instant('TRANS__JOB_STATUS__DELETED');
    });
  }

  retryJob(objectStatus?: MetaOperationObjectStatus) {
    let objectId = null;
    if (objectStatus) {
      objectId = objectStatus.object_id;
      objectStatus.status = 'retrying';
    } else {
      this.retryingAll = true;
      for (const objectStatus of this.message.details.object_statuses) {
        if (objectStatus.status === OperationExecutionStatus.FAILED ||
          objectStatus.status === OperationExecutionStatus.IN_PROGRESS ||
          objectStatus.status === OperationExecutionStatus.RUNNING) {
          objectStatus.status = 'retrying';
        }
      }
    }
    this.cms.retryOperationStep(this.message.queue_id, objectId).then((res) => {
      this.logger.info('Job retried successfully', res);
      this.message.statusMessage = this.translate.instant('TRANS__JOB_STATUS__RETRYING');
    }).catch(error => {
      this.logger.error('Error retrying job', error);
      this.message.status = 'failed';
      this.message.statusType = 'failed';
      this.message.statusMessage = this.translate.instant('TRANS__JOB_STATUS__FAILED');
    });
  }

  displayJobDetails() {
    if (this.message.details) {
      this.message.details = null; // Clear details if already displayed
      return;
    }
    const operationObjectStatusParams = {
      queue_id: this.message.queue_id,
      start_index: this.detailsStartIndex,
      rows: this.detailsRows,
      status_type: this.detailsStatusType
    };
    this.cms.getOperationObjectStatus(operationObjectStatusParams).then((res) => {
      this.logger.info('Job details fetched successfully');
      this.message.details = res;
      this.setObjectRef().then();
    }).catch(error => {
      this.logger.error('Error fetching job details', error);
      this.message.details = null;
    });
  }

  showNextObjects() {
    this.detailsStartIndex += this.detailsRows;
    const operationObjectStatusParams = {
      queue_id: this.message.queue_id,
      start_index: this.detailsStartIndex,
      rows: this.detailsRows,
      status_type: this.detailsStatusType
    };
    this.cms.getOperationObjectStatus(operationObjectStatusParams).then((res) => {
      this.logger.info('More job details fetched successfully');
      this.message.details = res;
      this.setObjectRef().then();
    });
  }

  showPreviousObjects() {
    if (this.detailsStartIndex >= this.detailsRows) {
      this.detailsStartIndex -= this.detailsRows;
      const operationObjectStatusParams = {
        queue_id: this.message.queue_id,
        start_index: this.detailsStartIndex,
        rows: this.detailsRows,
        status_type: this.detailsStatusType
      };
      this.cms.getOperationObjectStatus(operationObjectStatusParams).then((res) => {
        this.logger.info('Previous job details fetched successfully');
        this.message.details = res;
        this.setObjectRef().then();
      });
    }
  }

  getEndIndex(): number {
    return Math.min(this.detailsStartIndex + this.detailsRows, this.message.details.object_count)
  }

  getStatusText(status: string): string {
    switch (status) {
      case OperationExecutionStatus.IN_PROGRESS:
        return this.translate.instant('TRANS__JOB_STATUS__IN_PROGRESS');
      case OperationExecutionStatus.RUNNING:
        return this.translate.instant('TRANS__JOB_STATUS__RUNNING');
      case OperationExecutionStatus.OBJECT_PROCESSED:
        return this.translate.instant('TRANS__JOB_STATUS__OBJECT_PROCESSED');
      case OperationExecutionStatus.FAILED:
        return this.translate.instant('TRANS__JOB_STATUS__FAILED');
      case OperationExecutionStatus.OBJECT_RESTRICTED:
        return this.translate.instant('TRANS__JOB_STATUS__OBJECT_RESTRICTED');
      case OperationExecutionStatus.QUEUED:
        return this.translate.instant('TRANS__JOB_STATUS__QUEUED');
        case OperationExecutionStatus.RETRYING:
        return this.translate.instant('TRANS__JOB_STATUS__RETRYING');
    }
  }

  setDetailsStatusType(statusType: string) {
    this.message.details = null;
    this.detailsStatusType = statusType;
    this.detailsStartIndex = 0; // Reset start index when changing status type
    this.displayJobDetails();
  }

  private async setObjectRef() {
    for (const item of this.message.details.object_statuses) {
      const superobject = new SuperObjectModel()
      superobject.artifact_id = item.artifact_id;
      superobject.artifact_name = item.artifact_name;
      superobject.object_type = item.object_type;
      superobject.meta_type = item.meta_type;
      superobject.superobject_type_id = item.superobject_type_id;
      const refParams = {
        object: superobject
      };
      item.ref = await this.refService.makeRef(<RefParams>refParams);
      item.ref.routerLink = [...item.ref.routerLink, item.artifact_id];
    }
  }
}




